import { useMemo } from 'react';
import { z } from 'zod';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { ApiError } from './api';

/**
 * Cliente HTTP + hooks para workouts y dashboard.
 *
 * Es la **fuente única** de `PlannedWorkoutSchema` y `ActualWorkoutSchema`.
 * `lib/coach.ts` re-exporta `PlannedWorkoutSchema` desde aquí para no romper
 * imports existentes de la M1.4.
 *
 * Como en `strava.ts` y `coach.ts`, no usamos el helper `request` de `./api`
 * para mantener cada cliente con su propio mapeo de errores y permitir que
 * añadamos códigos específicos (p. ej. 409 cuando ya hay un plan, 422
 * cuando el `linked` no es válido) sin ensuciar el helper genérico.
 */

const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

// ---------------------------------------------------------------------------
// Constantes compartidas con `lib/coach.ts`
// ---------------------------------------------------------------------------

export const DISCIPLINES = ['swim', 'bike', 'run', 'strength', 'brick', 'other'] as const;
export type Discipline = (typeof DISCIPLINES)[number];

export const WORKOUT_STATUSES = ['planned', 'completed', 'skipped', 'partial'] as const;
export type WorkoutStatus = (typeof WORKOUT_STATUSES)[number];

export const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type DayKey = (typeof DAY_KEYS)[number];

/**
 * Zonas de intensidad — alineadas con
 * `backend/src/domain/workouts/intensity-zone.ts`. Soportan zonas numéricas
 * (z1..z7), alias descriptivos (recovery/threshold/vo2max) y `other` como
 * escape hatch.
 */
export const INTENSITY_ZONES = [
  'z1',
  'z2',
  'z3',
  'z4',
  'z5',
  'z6',
  'z7',
  'recovery',
  'threshold',
  'vo2max',
  'other',
] as const;
export type IntensityZone = (typeof INTENSITY_ZONES)[number];

/**
 * Disciplinas válidas para entrenos PLANIFICADOS — alineadas con
 * `backend/src/domain/workouts/discipline.ts PLANNED_DISCIPLINES`. El backend
 * rechaza `other` en planned (sesiones varias son sólo para actual).
 * Los días de descanso se modelan vía `weeklyPreferences.<day>='rest'`, no
 * como entreno planificado.
 */
export const PLANNED_DISCIPLINES = ['swim', 'bike', 'run', 'strength', 'brick'] as const;
export type PlannedDiscipline = (typeof PLANNED_DISCIPLINES)[number];

// ---------------------------------------------------------------------------
// Schemas: workouts
// ---------------------------------------------------------------------------

export const PlannedWorkoutSchema = z.object({
  id: z.number().int(),
  datetime: z.string(),
  timezone: z.string(),
  discipline: z.enum(DISCIPLINES),
  durationPlannedMin: z.number().nonnegative(),
  intensityZone: z.string().nullable(),
  description: z.string().nullable(),
  status: z.enum(WORKOUT_STATUSES),
  actualWorkoutId: z.number().int().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PlannedWorkout = z.infer<typeof PlannedWorkoutSchema>;

/**
 * `ActualWorkoutSchema` — refleja un workout real (importado típicamente de
 * Strava). Lo definimos con un núcleo pragmático y dejamos abiertos los
 * campos opcionales: el backend puede ir añadiendo (TSS, IF, etc.) sin que
 * tengamos que tocar el cliente.
 */
/**
 * Alineado con `backend/src/domain/workouts/workout-schemas.ts ActualWorkoutDTO`.
 * Campos en unidades nativas de Strava (metros para distancia, watts para
 * potencia). Si la UI quiere mostrar km, divide entre 1000.
 */
export const ActualWorkoutSchema = z.object({
  id: z.number().int(),
  source: z.string(),
  externalId: z.string().nullable(),
  /** ISO 8601 (alineado con el resto de la API). */
  externalUpdatedAt: z.string().nullable().optional(),
  datetime: z.string(),
  timezone: z.string(),
  discipline: z.enum(DISCIPLINES),
  durationMin: z.number().nonnegative(),
  distanceM: z.number().nonnegative().nullable(),
  avgHr: z.number().int().nullable(),
  avgPower: z.number().int().nullable(),
  tssEstimated: z.number().nullable().optional(),
  /** M2.1 — TSS calculado por el backend (puede ser null si no hay datos). */
  tssComputed: z.number().nullable().optional(),
  tssMethod: z.string().nullable().optional(),
  // -------------------------------------------------------------------------
  // M2.10 — métricas enriquecidas del summary de Strava.
  // -------------------------------------------------------------------------
  // Todas opcionales + nullable: las activities pre-M2.10 (importadas con el
  // mapper anterior) llegarán sin estos campos hasta que el usuario haga
  // re-sync manual desde /ajustes/strava. La UI debe ocultar las cards o
  // filas que estén vacías en vez de mostrar "—" en cada hueco.
  elevationGainM: z.number().int().nullable().optional(),
  avgSpeedMps: z.number().nullable().optional(),
  maxSpeedMps: z.number().nullable().optional(),
  maxHr: z.number().int().nullable().optional(),
  maxPower: z.number().int().nullable().optional(),
  normalizedPower: z.number().int().nullable().optional(),
  calories: z.number().int().nullable().optional(),
  kilojoules: z.number().nullable().optional(),
  deviceName: z.string().nullable().optional(),
  gearId: z.string().nullable().optional(),
  sufferScore: z.number().int().nullable().optional(),
  /** ISO 8601 (alineado con el resto de la API). */
  syncedAt: z.string().optional(),
});
export type ActualWorkout = z.infer<typeof ActualWorkoutSchema>;

// ---------------------------------------------------------------------------
// Schemas: dashboard
// ---------------------------------------------------------------------------

export const WeekTotalsSchema = z.object({
  plannedHours: z.number().nonnegative(),
  completedHours: z.number().nonnegative(),
  plannedCount: z.number().int().nonnegative(),
  completedCount: z.number().int().nonnegative(),
  byDiscipline: z
    .object({
      swim: z.number().nonnegative().optional(),
      bike: z.number().nonnegative().optional(),
      run: z.number().nonnegative().optional(),
      strength: z.number().nonnegative().optional(),
      brick: z.number().nonnegative().optional(),
      other: z.number().nonnegative().optional(),
    })
    .partial(),
});
export type WeekTotals = z.infer<typeof WeekTotalsSchema>;

export const WeekDaySchema = z.object({
  dateISO: z.string(),
  dayKey: z.enum(DAY_KEYS),
  workouts: z.array(PlannedWorkoutSchema),
  /** Actuals del día sin planned vinculado (M2.8). Opcional para retrocompat. */
  unplannedActuals: z.array(ActualWorkoutSchema).optional().default([]),
});
export type WeekDay = z.infer<typeof WeekDaySchema>;

export const WeekSchema = z.object({
  weekStartISO: z.string(),
  weekEndISO: z.string(),
  days: z.array(WeekDaySchema),
  totals: WeekTotalsSchema,
});
export type Week = z.infer<typeof WeekSchema>;

export const LoadWeekSchema = z.object({
  weekStartISO: z.string(),
  totalHours: z.number().nonnegative(),
});
export type LoadWeek = z.infer<typeof LoadWeekSchema>;

export const DashboardLoadSchema = z.object({
  weeks: z.array(LoadWeekSchema),
});
export type DashboardLoad = z.infer<typeof DashboardLoadSchema>;

export const DashboardStravaSchema = z.object({
  connected: z.boolean(),
  lastSyncAt: z.string().nullable().optional(),
  lastSyncCount: z.number().int().nonnegative().optional(),
});
export type DashboardStrava = z.infer<typeof DashboardStravaSchema>;

/**
 * M2.16 — el backend añade un bloque `kpis` opcional al response del
 * dashboard con KPIs derivados (monotonía, strain, ramp CTL, ACWR,
 * compliance, próxima carrera A, etc.). Lo declaramos como `z.unknown()`
 * para no acoplar este schema central al schema detallado vivo en
 * `lib/dashboard.ts` (evita dependencia circular). El consumidor en
 * `Dashboard.tsx` lo lee con `extractKpis()` y lo valida vía
 * `DashboardKpisSchema.safeParse()`.
 */
export const DashboardResponseSchema = z.object({
  profileName: z.string(),
  week: WeekSchema,
  nextWorkout: PlannedWorkoutSchema.nullable(),
  strava: DashboardStravaSchema,
  load: DashboardLoadSchema,
  kpis: z.unknown().optional(),
});
export type DashboardResponse = z.infer<typeof DashboardResponseSchema>;

// ---------------------------------------------------------------------------
// Schemas: workouts endpoint
// ---------------------------------------------------------------------------

export const WorkoutsResponseSchema = z.object({
  planned: z.array(PlannedWorkoutSchema),
  actual: z.array(ActualWorkoutSchema),
});
export type WorkoutsResponse = z.infer<typeof WorkoutsResponseSchema>;

// ---------------------------------------------------------------------------
// Schemas: recent workouts (M2.7)
// ---------------------------------------------------------------------------

/**
 * `GET /api/workouts/recent?limit=N` devuelve solo actuals (los planificados
 * sin actual asociado se ven en la vista semanal), ordenados más reciente
 * primero. El backend usa este endpoint para alimentar la card "Últimas
 * actividades" del dashboard.
 */
export const RecentWorkoutsResponseSchema = z.object({
  actual: z.array(ActualWorkoutSchema),
});
export type RecentWorkoutsResponse = z.infer<typeof RecentWorkoutsResponseSchema>;

/**
 * `GET /api/workouts/by-ids?ids=1,2,3` (M2.18) — resuelve un set pequeño
 * de actuals por id en una sola llamada. Usado por `DaySelectionPanel`
 * para mostrar el resumen de las actividades de un día (disciplina,
 * duración, distancia, TSS) sin pegar a `/detail` por cada una.
 */
export const ActualsByIdsResponseSchema = z.object({
  items: z.array(ActualWorkoutSchema),
});
export type ActualsByIdsResponse = z.infer<typeof ActualsByIdsResponseSchema>;

// ---------------------------------------------------------------------------
// Schemas: detalle de workout (M2.6)
// ---------------------------------------------------------------------------

/**
 * Slot de zona estimada para el detalle de un workout individual.
 * Reusamos la misma forma que `ZoneSlot` de `lib/load.ts` pero la mantenemos
 * local para que `workouts.ts` no dependa de `load.ts` y viceversa.
 */
export const WorkoutZoneSlotSchema = z.object({
  zone: z.string(),
  minutes: z.number().nonnegative(),
  percent: z.number().min(0).max(100),
});
export type WorkoutZoneSlot = z.infer<typeof WorkoutZoneSlotSchema>;

/**
 * Métodos de estimación de zonas. Alineado con backend
 * `domain/load/zone-estimation.ts`:
 * - `power`: zonas de potencia ciclista (Coggan, FTP).
 * - `hr`: zonas de frecuencia cardiaca (Friel, LTHR).
 * - `null`: no se pudo estimar (no hay FTP/LTHR o sin power/HR medio).
 */
export const ZONE_METHODS = ['power', 'hr'] as const;
export type ZoneMethod = (typeof ZONE_METHODS)[number] | null;

/**
 * `tssMethod` puede venir del backend como string libre (por ejemplo
 * 'power_normalized', 'hr_trimp', 'duration_fallback'). Lo dejamos abierto y
 * el cliente solo muestra el valor tal cual.
 */
export const ActualWorkoutDetailSchema = ActualWorkoutSchema.extend({
  tssComputed: z.number().nullable().optional(),
  tssMethod: z.string().nullable().optional(),
});
export type ActualWorkoutDetail = z.infer<typeof ActualWorkoutDetailSchema>;

export const WorkoutDetailResponseSchema = z.object({
  actual: ActualWorkoutDetailSchema,
  planned: PlannedWorkoutSchema.nullable(),
  zones: z.array(WorkoutZoneSlotSchema),
  zoneMethod: z.enum(ZONE_METHODS).nullable(),
});
export type WorkoutDetailResponse = z.infer<typeof WorkoutDetailResponseSchema>;

// ---------------------------------------------------------------------------
// Schemas: search (M2.13)
// ---------------------------------------------------------------------------

export const WorkoutsSearchFiltersSchema = z.object({
  fromISO: z.string().optional(),
  toISO: z.string().optional(),
  disciplines: z.array(z.enum(DISCIPLINES)).optional(),
  minDistanceM: z.number().int().nonnegative().optional(),
  minDurationMin: z.number().int().nonnegative().optional(),
  source: z.enum(['strava', 'manual']).optional(),
  q: z.string().optional(),
  sort: z.enum(['date', 'duration', 'distance', 'tss', 'np', 'hr']).default('date'),
  order: z.enum(['asc', 'desc']).default('desc'),
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
});
export type WorkoutsSearchFilters = z.infer<typeof WorkoutsSearchFiltersSchema>;

export const WorkoutsSearchResponseSchema = z.object({
  items: z.array(ActualWorkoutSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(100),
});
export type WorkoutsSearchResponse = z.infer<typeof WorkoutsSearchResponseSchema>;

// ---------------------------------------------------------------------------
// Schemas: streams + splits (M2.15)
// ---------------------------------------------------------------------------

/**
 * Cada split parsed desde `splits_metric` de Strava. Todos los campos
 * "métricos" son nullable porque Strava puede no exponerlos para la
 * disciplina (p.ej. avgPower nunca viene en splits por km).
 */
export const WorkoutSplitSchema = z.object({
  km: z.number().int().positive(),
  timeS: z.number().int().nonnegative(),
  paceSecPerKm: z.number().int().nullable(),
  avgHr: z.number().int().nullable(),
  avgPower: z.number().int().nullable(),
  elevDiffM: z.number().nullable(),
});
export type WorkoutSplit = z.infer<typeof WorkoutSplitSchema>;

/**
 * M2.40 — split de natación cada ~100m, derivado del backend que agrupa
 * `laps[]` de Strava (1 lap por largo de piscina) en bloques de 100m. El
 * último bloque puede ser menor (50m / 75m) si la distancia total no es
 * múltiplo exacto de 100.
 */
export const WorkoutSwimSplitSchema = z.object({
  block: z.number().int().positive(),
  distanceM: z.number().positive(),
  timeS: z.number().nonnegative(),
  paceSecPer100m: z.number().nullable(),
  avgHr: z.number().int().nullable(),
});
export type WorkoutSwimSplit = z.infer<typeof WorkoutSwimSplitSchema>;

/**
 * Streams temporales (segundo a segundo) parsed desde `/activities/:id/streams`.
 * Todos los arrays paralelos al length de `timeS`. Cada array opcional puede
 * ser null si Strava no lo entregó (sensor sin medidor, etc.).
 */
export const WorkoutStreamsSchema = z.object({
  timeS: z.array(z.number()),
  heartrateBpm: z.array(z.number().nullable()).nullable(),
  watts: z.array(z.number().nullable()).nullable(),
  velocityMps: z.array(z.number().nullable()).nullable(),
  altitudeM: z.array(z.number().nullable()).nullable(),
  cadenceRpm: z.array(z.number().nullable()).nullable(),
  distanceM: z.array(z.number().nullable()).nullable(),
});
export type WorkoutStreams = z.infer<typeof WorkoutStreamsSchema>;

/**
 * Response del endpoint /api/workouts/:id/streams. Si `streams` es null y
 * `reason` está presente, el frontend muestra el motivo en el espacio
 * reservado para el gráfico.
 */
export const WorkoutStreamsResponseSchema = z.object({
  cached: z.boolean(),
  fetchedAt: z.string().nullable(),
  streams: WorkoutStreamsSchema.nullable(),
  splits: z.array(WorkoutSplitSchema).nullable(),
  /**
   * M2.40 — splits cada 100m para natación. Backend devuelve `null` para
   * actuals que no son swim, o cuando Strava no entregó laps utilizables.
   * Es `optional` (no `default(null)`) para que en el tipo inferido el
   * campo sea `T | null | undefined` y los tests + callers existentes
   * pre-M2.40 sigan siendo compatibles sin pasar el campo explícitamente.
   */
  splitsSwim: z.array(WorkoutSwimSplitSchema).nullable().optional(),
  reason: z.string().optional(),
});
export type WorkoutStreamsResponse = z.infer<typeof WorkoutStreamsResponseSchema>;

export const AutoLinkResponseSchema = z.object({
  linked: z.array(
    z.object({
      plannedId: z.number().int(),
      actualId: z.number().int(),
    }),
  ),
  count: z.number().int().nonnegative(),
});
export type AutoLinkResponse = z.infer<typeof AutoLinkResponseSchema>;

// ---------------------------------------------------------------------------
// HTTP helpers
// ---------------------------------------------------------------------------

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function postJson<T>(
  path: string,
  schema: z.ZodType<T>,
  body: unknown,
  method: 'POST' | 'PATCH' = 'POST',
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (!response.ok) {
    throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
  }
  const json = await readJsonSafe(response);
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(`Respuesta ${path} no cumple esquema`, response.status, parsed.error);
  }
  return parsed.data;
}

async function getJson<T>(path: string, schema: z.ZodType<T>): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (!response.ok) {
    throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
  }
  const json = await readJsonSafe(response);
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(`Respuesta ${path} no cumple esquema`, response.status, parsed.error);
  }
  return parsed.data;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/**
 * GET /api/dashboard?weekStartISO=ISO
 *
 * Si `weekStartISO` es `undefined`, el backend devuelve la semana actual.
 */
export function getDashboard(weekStartISO?: string): Promise<DashboardResponse> {
  const qs = weekStartISO ? `?weekStartISO=${encodeURIComponent(weekStartISO)}` : '';
  return getJson(`/api/dashboard${qs}`, DashboardResponseSchema);
}

export function getWorkoutsInRange(fromISO: string, toISO: string): Promise<WorkoutsResponse> {
  const qs = `?from=${encodeURIComponent(fromISO)}&to=${encodeURIComponent(toISO)}`;
  return getJson(`/api/workouts${qs}`, WorkoutsResponseSchema);
}

/**
 * GET /api/workouts/recent?limit=N
 *
 * Por defecto pide 10 últimas actuals. El backend valida el límite y aplica
 * un máximo sano (~50) por seguridad.
 */
export function getRecentWorkouts(limit = 10): Promise<RecentWorkoutsResponse> {
  return getJson(
    `/api/workouts/recent?limit=${encodeURIComponent(String(limit))}`,
    RecentWorkoutsResponseSchema,
  );
}

/**
 * GET /api/workouts/by-ids?ids=1,2,3 (M2.18). Devuelve un set de actuals
 * resueltos por id, en el mismo orden de los ids dados (ids inexistentes
 * o de otro profile se omiten silenciosamente).
 */
export function getActualsByIds(ids: number[]): Promise<ActualsByIdsResponse> {
  if (ids.length === 0) return Promise.resolve({ items: [] });
  const qs = encodeURIComponent(ids.join(','));
  return getJson(`/api/workouts/by-ids?ids=${qs}`, ActualsByIdsResponseSchema);
}

/**
 * GET /api/workouts/:id/detail
 *
 * Devuelve un ActualWorkout enriquecido con el planned vinculado (si
 * existe) y la estimación de zonas. Si no hay datos suficientes para
 * estimar zonas, `zones` es `[]` y `zoneMethod` es `'none'`.
 */
export function getWorkoutDetail(id: number): Promise<WorkoutDetailResponse> {
  return getJson(`/api/workouts/${encodeURIComponent(String(id))}/detail`, WorkoutDetailResponseSchema);
}

/**
 * GET /api/workouts/search?from=...&to=...&disciplines=swim,bike&...
 *
 * Listado paginado + filtrable de actuals. Usado por `/entrenos`.
 */
export function getWorkoutsSearch(
  filters: WorkoutsSearchFilters,
): Promise<WorkoutsSearchResponse> {
  const params = new URLSearchParams();
  if (filters.fromISO) params.set('from', filters.fromISO);
  if (filters.toISO) params.set('to', filters.toISO);
  if (filters.disciplines && filters.disciplines.length > 0) {
    params.set('disciplines', filters.disciplines.join(','));
  }
  if (typeof filters.minDistanceM === 'number') {
    params.set('minDistanceM', String(filters.minDistanceM));
  }
  if (typeof filters.minDurationMin === 'number') {
    params.set('minDurationMin', String(filters.minDurationMin));
  }
  if (filters.source) params.set('source', filters.source);
  if (filters.q && filters.q.trim().length > 0) params.set('q', filters.q.trim());
  params.set('sort', filters.sort);
  params.set('order', filters.order);
  params.set('page', String(filters.page));
  params.set('pageSize', String(filters.pageSize));
  return getJson(`/api/workouts/search?${params.toString()}`, WorkoutsSearchResponseSchema);
}

/**
 * GET /api/workouts/:id/streams
 *
 * Lee streams cacheados, o dispara on-demand fetch a Strava (con upsert a
 * cache local) si no hay nada todavía. Si la actividad no es de Strava o no
 * hay credenciales, devuelve `{streams: null, splits: null, reason: ...}`.
 */
export function getWorkoutStreams(id: number): Promise<WorkoutStreamsResponse> {
  return getJson(
    `/api/workouts/${encodeURIComponent(String(id))}/streams`,
    WorkoutStreamsResponseSchema,
  );
}

export function markWorkoutStatus(id: number, status: WorkoutStatus): Promise<PlannedWorkout> {
  return postJson(`/api/workouts/planned/${id}`, PlannedWorkoutSchema, { status }, 'PATCH');
}

// ---------------------------------------------------------------------------
// M2.46.A — CRUD manual de planned workouts
// ---------------------------------------------------------------------------
//
// Endpoints existentes: POST /api/workouts/planned (201), PATCH /api/workouts/
// planned/:id (200), DELETE /api/workouts/planned/:id (204). Schema body
// alineado con `backend/src/domain/workouts/workout-schemas.ts`.

/**
 * Body para crear un planned workout. `datetime` debe ser ISO 8601 con offset.
 * `intensityZone` y `description` son opcionales. El backend rechaza
 * `discipline='other'` y `durationPlannedMin` fuera de [1,1440].
 */
export interface PlannedWorkoutCreateBody {
  datetime: string;
  timezone: string;
  discipline: PlannedDiscipline;
  durationPlannedMin: number;
  intensityZone?: IntensityZone | null;
  description?: string | null;
}

/**
 * Patch parcial — todos los campos opcionales. Backend rechaza patch vacío.
 * `status` también se puede patchear pero NO se permite `completed` sin un
 * actual vinculado (el backend devuelve 409 en ese caso).
 */
export interface PlannedWorkoutPatchBody {
  datetime?: string;
  timezone?: string;
  discipline?: PlannedDiscipline;
  durationPlannedMin?: number;
  intensityZone?: IntensityZone | null;
  description?: string | null;
  status?: WorkoutStatus;
}

export function createPlannedWorkout(
  body: PlannedWorkoutCreateBody,
): Promise<PlannedWorkout> {
  return postJson('/api/workouts/planned', PlannedWorkoutSchema, body, 'POST');
}

export function updatePlannedWorkout(
  id: number,
  patch: PlannedWorkoutPatchBody,
): Promise<PlannedWorkout> {
  return postJson(
    `/api/workouts/planned/${id}`,
    PlannedWorkoutSchema,
    patch,
    'PATCH',
  );
}

export async function deletePlannedWorkout(id: number): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/workouts/planned/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
  } catch (err) {
    throw new ApiError('Error de red al borrar el entreno', 0, err);
  }
  // 204 No Content es el happy path; el backend devuelve 404 si no existe
  // (lo dejamos pasar como error tipo ApiError para que la UI muestre toast).
  if (response.status === 204) return;
  if (!response.ok) {
    throw new ApiError(
      `No se pudo borrar el entreno: ${response.status}`,
      response.status,
    );
  }
}

export function linkWorkouts(plannedId: number, actualId: number | null): Promise<PlannedWorkout> {
  return postJson('/api/workouts/link', PlannedWorkoutSchema, { plannedId, actualId }, 'POST');
}

export function autoLinkInRange(
  fromISO?: string,
  toISO?: string,
  toleranceHours?: number,
): Promise<AutoLinkResponse> {
  const body: Record<string, unknown> = {};
  if (fromISO) body.fromISO = fromISO;
  if (toISO) body.toISO = toISO;
  if (typeof toleranceHours === 'number') body.toleranceHours = toleranceHours;
  return postJson('/api/workouts/auto-link', AutoLinkResponseSchema, body, 'POST');
}

// ---------------------------------------------------------------------------
// Hooks (TanStack Query)
// ---------------------------------------------------------------------------

export const DASHBOARD_QUERY_KEY = ['dashboard'] as const;
export const WORKOUTS_QUERY_KEY = ['workouts'] as const;
export const WORKOUT_DETAIL_QUERY_KEY = ['workouts', 'detail'] as const;
export const WORKOUTS_RECENT_QUERY_KEY = ['workouts', 'recent'] as const;
export const WORKOUTS_RANGE_QUERY_KEY = ['workouts', 'range'] as const;
export const WORKOUTS_SEARCH_QUERY_KEY = ['workouts', 'search'] as const;
export const WORKOUT_STREAMS_QUERY_KEY = ['workouts', 'streams'] as const;

export function useDashboard(
  weekStartISO?: string,
): UseQueryResult<DashboardResponse, Error> {
  return useQuery<DashboardResponse, Error>({
    queryKey: [...DASHBOARD_QUERY_KEY, weekStartISO ?? null],
    queryFn: () => getDashboard(weekStartISO),
    staleTime: 30_000,
    retry: 1,
  });
}

/**
 * Hook para `GET /api/workouts/recent?limit=N`. Usado por la card "Últimas
 * actividades" del dashboard. La invalidación viene a través del prefijo
 * `WORKOUTS_QUERY_KEY` (las mutaciones `markWorkoutStatus`, `linkWorkouts` y
 * `autoLinkInRange` ya invalidan `['workouts']`).
 */
export function useRecentWorkouts(
  limit = 10,
): UseQueryResult<RecentWorkoutsResponse, Error> {
  return useQuery<RecentWorkoutsResponse, Error>({
    queryKey: [...WORKOUTS_RECENT_QUERY_KEY, limit],
    queryFn: () => getRecentWorkouts(limit),
    staleTime: 30_000,
    retry: 1,
  });
}

/**
 * Hook para `GET /api/workouts/by-ids` (M2.18). Si `ids` está vacío, la
 * query queda en estado "idle" sin pegar al servidor (return data con
 * `items: []` para que el consumidor pueda renderizar sin ramificar).
 */
export function useActualsByIds(
  ids: number[],
): UseQueryResult<ActualsByIdsResponse, Error> {
  // Sorteo para que el queryKey sea estable independientemente del orden.
  const stable = useMemo(() => [...ids].sort((a, b) => a - b), [ids]);
  return useQuery<ActualsByIdsResponse, Error>({
    queryKey: [...WORKOUTS_QUERY_KEY, 'by-ids', stable],
    queryFn: () => getActualsByIds(stable),
    enabled: stable.length > 0,
    staleTime: 60_000,
    retry: 1,
  });
}

/**
 * Hook para `GET /api/workouts?from=ISO&to=ISO`. Usado por el calendario
 * mensual del dashboard para pintar dots en los días con actividad. Las
 * dos fechas se serializan en la query key, así que cambiar de mes
 * dispara un fetch nuevo (y mantiene en cache los meses ya vistos).
 */
export function useWorkoutsInRange(
  fromISO: string,
  toISO: string,
): UseQueryResult<WorkoutsResponse, Error> {
  return useQuery<WorkoutsResponse, Error>({
    queryKey: [...WORKOUTS_RANGE_QUERY_KEY, fromISO, toISO],
    queryFn: () => getWorkoutsInRange(fromISO, toISO),
    staleTime: 30_000,
    retry: 1,
  });
}

export function useWorkoutDetail(
  id: number | null | undefined,
): UseQueryResult<WorkoutDetailResponse, Error> {
  return useQuery<WorkoutDetailResponse, Error>({
    queryKey: [...WORKOUT_DETAIL_QUERY_KEY, id ?? null],
    queryFn: () => getWorkoutDetail(id as number),
    enabled: typeof id === 'number' && Number.isFinite(id),
    staleTime: 30_000,
    retry: 1,
  });
}

/**
 * Hook para `GET /api/workouts/search`. La query key serializa TODOS los
 * filtros, así que cambiar cualquier filtro / orden / página dispara un
 * fetch nuevo (manteniendo en cache las búsquedas previas).
 */
export function useWorkoutsSearch(
  filters: WorkoutsSearchFilters,
): UseQueryResult<WorkoutsSearchResponse, Error> {
  return useQuery<WorkoutsSearchResponse, Error>({
    queryKey: [
      ...WORKOUTS_SEARCH_QUERY_KEY,
      filters.fromISO ?? null,
      filters.toISO ?? null,
      filters.disciplines?.slice().sort().join(',') ?? null,
      filters.minDistanceM ?? null,
      filters.minDurationMin ?? null,
      filters.source ?? null,
      filters.q ?? null,
      filters.sort,
      filters.order,
      filters.page,
      filters.pageSize,
    ],
    queryFn: () => getWorkoutsSearch(filters),
    staleTime: 30_000,
    retry: 1,
  });
}

/**
 * Hook para `GET /api/workouts/:id/streams`. Lazy por defecto vía `enabled`:
 * la card de streams puede cargar al expandirse para no martillar a Strava al
 * abrir cada detalle. `staleTime` infinito porque los streams de una activity
 * pasada NO cambian — solo se refresca si el usuario invalida manualmente.
 */
export function useWorkoutStreams(
  id: number | null | undefined,
  enabled = true,
): UseQueryResult<WorkoutStreamsResponse, Error> {
  return useQuery<WorkoutStreamsResponse, Error>({
    queryKey: [...WORKOUT_STREAMS_QUERY_KEY, id ?? null],
    queryFn: () => getWorkoutStreams(id as number),
    enabled: enabled && typeof id === 'number' && Number.isFinite(id),
    staleTime: Infinity,
    retry: 1,
  });
}

export function useMarkWorkout(): UseMutationResult<
  PlannedWorkout,
  Error,
  { id: number; status: WorkoutStatus }
> {
  const qc = useQueryClient();
  return useMutation<PlannedWorkout, Error, { id: number; status: WorkoutStatus }>({
    mutationFn: ({ id, status }) => markWorkoutStatus(id, status),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
      await qc.invalidateQueries({ queryKey: WORKOUTS_QUERY_KEY });
    },
  });
}

export function useLinkWorkouts(): UseMutationResult<
  PlannedWorkout,
  Error,
  { plannedId: number; actualId: number | null }
> {
  const qc = useQueryClient();
  return useMutation<PlannedWorkout, Error, { plannedId: number; actualId: number | null }>({
    mutationFn: ({ plannedId, actualId }) => linkWorkouts(plannedId, actualId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
      await qc.invalidateQueries({ queryKey: WORKOUTS_QUERY_KEY });
    },
  });
}

export function useAutoLink(): UseMutationResult<
  AutoLinkResponse,
  Error,
  { fromISO?: string; toISO?: string; toleranceHours?: number } | undefined
> {
  const qc = useQueryClient();
  return useMutation<
    AutoLinkResponse,
    Error,
    { fromISO?: string; toISO?: string; toleranceHours?: number } | undefined
  >({
    mutationFn: (args) =>
      autoLinkInRange(args?.fromISO, args?.toISO, args?.toleranceHours),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
      await qc.invalidateQueries({ queryKey: WORKOUTS_QUERY_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// M2.46.A — Hooks de CRUD manual
// ---------------------------------------------------------------------------
//
// Invalidan TODO el árbol `['workouts']` + el dashboard para forzar refetch
// del calendario, listado y heatmap. La granularidad fina (sólo el día
// afectado) sería mejor pero el coste actual es bajo (queries < 100ms).

export function useCreatePlannedWorkout(): UseMutationResult<
  PlannedWorkout,
  Error,
  PlannedWorkoutCreateBody
> {
  const qc = useQueryClient();
  return useMutation<PlannedWorkout, Error, PlannedWorkoutCreateBody>({
    mutationFn: (body) => createPlannedWorkout(body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
      await qc.invalidateQueries({ queryKey: WORKOUTS_QUERY_KEY });
    },
  });
}

export function useUpdatePlannedWorkout(): UseMutationResult<
  PlannedWorkout,
  Error,
  { id: number; patch: PlannedWorkoutPatchBody }
> {
  const qc = useQueryClient();
  return useMutation<
    PlannedWorkout,
    Error,
    { id: number; patch: PlannedWorkoutPatchBody }
  >({
    mutationFn: ({ id, patch }) => updatePlannedWorkout(id, patch),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
      await qc.invalidateQueries({ queryKey: WORKOUTS_QUERY_KEY });
    },
  });
}

export function useDeletePlannedWorkout(): UseMutationResult<
  void,
  Error,
  number
> {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) => deletePlannedWorkout(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
      await qc.invalidateQueries({ queryKey: WORKOUTS_QUERY_KEY });
    },
  });
}
